import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Phone, CheckCircle2, Clock, 
  Plus, MessageSquare,
  TrendingUp, Target, Briefcase, RefreshCw, Trash2, Bell, AlarmClock
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/MainLayout';

const ROUTINE_BLOCKS = [
  {
    id: 'outreach',
    title: 'Outreach',
    icon: Phone,
    color: 'bg-sky-500',
    lightColor: 'bg-sky-50',
    textColor: 'text-sky-700',
    description: 'New contacts & cold calls',
    target: 5,
    miniFlows: [
      { action: 'Make cold call', script: 'cold_call' },
      { action: 'Send intro email', script: 'follow_up' },
      { action: 'LinkedIn connect', script: null },
      { action: 'Research new prospect', script: null },
      { action: 'Log outreach result', script: null },
    ]
  },
  {
    id: 'follow_up',
    title: 'Follow-ups',
    icon: MessageSquare,
    color: 'bg-emerald-500',
    lightColor: 'bg-emerald-50',
    textColor: 'text-emerald-700',
    description: 'Pending responses & quotes',
    target: 3,
    miniFlows: [
      { action: 'Chase quote response', script: 'closing' },
      { action: 'Follow up meeting', script: 'follow_up' },
      { action: 'Send reminder', script: 'follow_up' },
      { action: 'Check sequence due steps', script: null },
      { action: 'Book follow-up call', script: null },
    ]
  },
  {
    id: 'visibility',
    title: 'Visibility',
    icon: TrendingUp,
    color: 'bg-violet-500',
    lightColor: 'bg-violet-50',
    textColor: 'text-violet-700',
    description: 'Brand presence & networking',
    target: 2,
    miniFlows: [
      { action: 'Post on LinkedIn', script: null },
      { action: 'Comment on industry news', script: null },
      { action: 'Schedule networking event', script: null },
      { action: 'Share case study', script: null },
    ]
  },
  {
    id: 'pipeline_hygiene',
    title: 'Pipeline Hygiene',
    icon: RefreshCw,
    color: 'bg-amber-500',
    lightColor: 'bg-amber-50',
    textColor: 'text-amber-700',
    description: 'Data quality & updates',
    target: 2,
    miniFlows: [
      { action: 'Update contact info', script: null },
      { action: 'Review stale leads', script: null },
      { action: 'Archive old enquiries', script: null },
      { action: 'Check quote deadlines', script: null },
      { action: 'Review dormant contacts', script: null },
    ]
  }
];

export const TodayPage = () => {
  const { user, authFetch } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState({});
  const [categoryStats, setCategoryStats] = useState({});
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentActions, setRecentActions] = useState([]);
  const [routinePrompts, setRoutinePrompts] = useState([]);
  const [customQuickActions, setCustomQuickActions] = useState(null);
  
  // Daily Reminders
  const [reminders, setReminders] = useState([]);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderForm, setReminderForm] = useState({
    title: '',
    time: '09:00',
  });

  // Get user's local date in YYYY-MM-DD format for API calls
  // This is the canonical "today" from the user's perspective
  const getLocalDateString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const today = new Date().toLocaleDateString('en-GB', { 
    weekday: 'long', 
    day: 'numeric', 
    month: 'long' 
  });

  // Store local date for API calls
  const localDate = getLocalDateString();

  const loadData = useCallback(async () => {
    try {
      // Pass user's local date to all Today endpoints for timezone-aware queries
      const dateParam = `?date=${localDate}`;
      const [tasksRes, summaryRes, actionsRes, promptsRes, remindersRes, quickActionsRes] = await Promise.all([
        authFetch(`/today/tasks${dateParam}`),
        authFetch(`/today/summary${dateParam}`),
        authFetch('/actions/recent?limit=5'),
        authFetch('/prompts/routine'),
        authFetch(`/today/reminders${dateParam}`),
        authFetch('/routines/quick-actions')
      ]);
      
      if (tasksRes.ok) {
        const data = await tasksRes.json();
        setTasks(data.tasks || {});
        setCategoryStats(data.category_stats || {});
      }
      
      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setSummary(data);
      }
      
      if (actionsRes.ok) {
        const data = await actionsRes.json();
        setRecentActions(data.actions || []);
      }
      
      if (promptsRes.ok) {
        const data = await promptsRes.json();
        setRoutinePrompts(data.prompts || []);
      }
      
      if (remindersRes.ok) {
        const data = await remindersRes.json();
        setReminders(data.reminders || []);
      }
      
      if (quickActionsRes.ok) {
        const data = await quickActionsRes.json();
        setCustomQuickActions(data.quick_actions || null);
      }
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch, localDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleTask = async (taskId, currentStatus) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    try {
      const res = await authFetch(`/today/tasks/${taskId}`, {
        method: 'PATCH',
        body: { status: newStatus }
      });
      
      if (res.ok) {
        toast.success(newStatus === 'completed' ? 'Task completed' : 'Task unchecked');
        loadData();
      } else {
        const err = await res.json().catch(() => ({}));
        console.error('Task update failed:', res.status, err);
        
        // Handle daily limit errors (string or structured)
        if (err.detail?.error_code === 'DAILY_TARGET_REACHED') {
          toast.error(`Daily target already reached for ${err.detail.category?.replace('_', ' ') || 'tasks'} today.`);
        } else if (typeof err.detail === 'string' && err.detail.includes('Daily limit reached')) {
          // Parse: "Daily limit reached for visibility. Target: X, Completed: Y, Date: Z"
          const match = err.detail.match(/Daily limit reached for (\w+)/i);
          const category = match ? match[1].replace('_', ' ') : 'tasks';
          toast.error(`Daily target already reached for ${category} today.`);
        } else if (typeof err.detail === 'string') {
          toast.error(err.detail);
        } else if (err.detail?.message) {
          toast.error(err.detail.message);
        } else {
          toast.error(`Failed to update task (${res.status})`);
        }
      }
    } catch (err) {
      console.error('Task update error:', err);
      toast.error('Failed to update task - please check your connection');
    }
  };

  const createQuickTask = async (category, title) => {
    try {
      const res = await authFetch('/today/tasks', {
        method: 'POST',
        body: {
          title,
          category,
          due_date: localDate  // Use user's local date, not UTC
        }
      });
      
      if (res.ok) {
        toast.success('Task created');
        loadData();
      }
    } catch (err) {
      toast.error('Failed to create task');
    }
  };

  const deleteTask = async (taskId) => {
    try {
      const res = await authFetch(`/today/tasks/${taskId}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        toast.success('Task removed');
        loadData();
      } else {
        toast.error('Failed to remove task');
      }
    } catch (err) {
      toast.error('Failed to remove task');
    }
  };

  // Daily Reminder Functions
  const addReminder = async () => {
    if (!reminderForm.title.trim()) return;
    
    try {
      const res = await authFetch('/today/reminders', {
        method: 'POST',
        body: {
          title: reminderForm.title,
          time: reminderForm.time,
          date: localDate  // Use user's local date for timezone correctness
        }
      });
      
      if (res.ok) {
        toast.success('Reminder added');
        setShowReminderModal(false);
        setReminderForm({ title: '', time: '09:00' });
        loadData();
      } else {
        toast.error('Failed to add reminder');
      }
    } catch (err) {
      toast.error('Failed to add reminder');
    }
  };

  const dismissReminder = async (reminderId) => {
    try {
      // Optimistic update
      setReminders(prev => prev.map(r => 
        r.id === reminderId ? { ...r, status: 'dismissed' } : r
      ));
      
      const res = await authFetch(`/today/reminders/${reminderId}/dismiss`, {
        method: 'POST'
      });
      
      if (res.ok) {
        toast.success('Reminder dismissed');
      } else {
        // Revert on failure
        loadData();
      }
    } catch (err) {
      loadData();
      toast.error('Failed to dismiss reminder');
    }
  };

  const deleteReminder = async (reminderId) => {
    try {
      // Optimistic update - remove from UI immediately
      setReminders(prev => prev.filter(r => r.id !== reminderId));
      
      const res = await authFetch(`/today/reminders/${reminderId}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        toast.success('Reminder deleted');
      } else {
        // Revert on failure
        loadData();
        toast.error('Failed to delete reminder');
      }
    } catch (err) {
      // Revert on error
      loadData();
      toast.error('Failed to delete reminder');
    }
  };

  const logAction = async (actionType, entityType, details = {}) => {
    try {
      await authFetch('/actions/log', {
        method: 'POST',
        body: { action_type: actionType, entity_type: entityType, details }
      });
      loadData();
    } catch (err) {
      console.error('Log error:', err);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-slate-500">Loading...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div data-testid="today-page" className="max-w-full overflow-hidden">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
          <div className="min-w-0">
            <p className="text-xs md:text-sm font-medium text-slate-500 uppercase tracking-wider mb-1">
              {today}
            </p>
            <h1 className="font-heading text-xl md:text-3xl font-bold text-[#1e3a5f] truncate">
              {getGreeting()}, {user?.first_name}
            </h1>
          </div>
          <Button
            onClick={() => navigate('/enquiry/new')}
            className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-full px-4 md:px-6 h-10 md:h-11 font-semibold text-sm flex-shrink-0"
            data-testid="new-enquiry-btn"
          >
            <Plus className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">New Enquiry</span>
            <span className="md:hidden">New</span>
          </Button>
        </div>
        
        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4 mb-6 md:mb-10">
          <div className="bg-white rounded-xl md:rounded-2xl p-3 md:p-5 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl bg-[#1e3a5f]/10 flex items-center justify-center flex-shrink-0">
                <Target className="w-4 h-4 md:w-5 md:h-5 text-[#1e3a5f]" />
              </div>
              <span className="text-xs md:text-sm font-medium text-slate-500 truncate">Tasks</span>
            </div>
            <p className="font-heading text-xl md:text-3xl font-bold text-[#1e3a5f]">
              {summary?.completed_tasks || 0}/{summary?.total_tasks || 0}
            </p>
          </div>
          
          <div className="bg-white rounded-xl md:rounded-2xl p-3 md:p-5 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-emerald-600" />
              </div>
              <span className="text-xs md:text-sm font-medium text-slate-500 truncate">Done</span>
            </div>
            <p className="font-heading text-xl md:text-3xl font-bold text-emerald-600">
              {summary?.completion_rate || 0}%
            </p>
          </div>
          
          <div 
            className="bg-white rounded-xl md:rounded-2xl p-3 md:p-5 border border-slate-100 shadow-sm cursor-pointer hover:shadow-md hover:border-amber-200 transition-all"
            onClick={() => {
              // Scroll to tasks section and filter to pending
              const tasksSection = document.getElementById('tasks-section');
              if (tasksSection) {
                tasksSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            data-testid="pending-tile"
          >
            <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                <Clock className="w-4 h-4 md:w-5 md:h-5 text-amber-600" />
              </div>
              <span className="text-xs md:text-sm font-medium text-slate-500 truncate">Pending Tasks</span>
            </div>
            <p className="font-heading text-xl md:text-3xl font-bold text-amber-600">
              {summary?.pending_today || 0}
            </p>
            <p className="text-[10px] md:text-xs text-slate-400 mt-1">
              Tap to view
            </p>
          </div>
          
          <div className="bg-white rounded-xl md:rounded-2xl p-3 md:p-5 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl bg-sky-50 flex items-center justify-center flex-shrink-0">
                <Briefcase className="w-4 h-4 md:w-5 md:h-5 text-sky-600" />
              </div>
              <span className="text-xs md:text-sm font-medium text-slate-500 truncate">Role</span>
            </div>
            <p className="font-heading text-sm md:text-lg font-bold text-sky-600 capitalize truncate">
              {user?.role?.replace('_', ' ')}
            </p>
          </div>
        </div>

        {/* Daily Reminders Section */}
        <div className="mb-6 md:mb-10">
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <h2 className="font-heading text-base md:text-lg font-semibold text-[#1e3a5f] flex items-center gap-2">
              <Bell className="w-4 h-4 md:w-5 md:h-5" />
              Reminders
            </h2>
            <Button
              onClick={() => setShowReminderModal(true)}
              variant="outline"
              size="sm"
              className="rounded-full h-8 px-3 text-xs"
              data-testid="add-reminder-btn"
            >
              <Plus className="w-3 h-3 md:w-4 md:h-4 md:mr-1" />
              <span className="hidden md:inline">Add Reminder</span>
              <span className="md:hidden">Add</span>
            </Button>
          </div>
          
          {reminders.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {reminders.map((reminder) => (
                <div
                  key={reminder.id}
                  className={`p-4 rounded-xl border transition-all ${
                    reminder.status === 'dismissed' 
                      ? 'bg-slate-50 border-slate-200 opacity-60' 
                      : 'bg-amber-50 border-amber-200'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        reminder.status === 'dismissed' ? 'bg-slate-200' : 'bg-amber-200'
                      }`}>
                        <AlarmClock className={`w-4 h-4 ${
                          reminder.status === 'dismissed' ? 'text-slate-500' : 'text-amber-700'
                        }`} />
                      </div>
                      <div>
                        <p className={`font-medium ${
                          reminder.status === 'dismissed' ? 'text-slate-400 line-through' : 'text-slate-800'
                        }`}>
                          {reminder.title}
                        </p>
                        <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                          <Clock className="w-3 h-3" />
                          {reminder.time}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {reminder.status !== 'dismissed' && (
                        <button
                          onClick={() => dismissReminder(reminder.id)}
                          className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-100"
                          title="Mark done"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => deleteReminder(reminder.id)}
                        className="p-1.5 rounded-lg text-red-500 hover:bg-red-100"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-slate-50 rounded-xl border border-slate-100">
              <Bell className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500">No reminders set for today</p>
              <Button
                onClick={() => setShowReminderModal(true)}
                variant="outline"
                size="sm"
                className="mt-3 rounded-full"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Your First Reminder
              </Button>
            </div>
          )}
        </div>

        {/* System Prompts Banner */}
        {routinePrompts.length > 0 && (
          <div className="mb-6 space-y-2">
            {routinePrompts.slice(0, 3).map((prompt, idx) => (
              <div 
                key={idx}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl ${
                  prompt.priority === 'high' 
                    ? 'bg-amber-50 border border-amber-200' 
                    : 'bg-slate-50 border border-slate-200'
                }`}
              >
                <div className={`w-2 h-2 rounded-full ${
                  prompt.priority === 'high' ? 'bg-amber-500 animate-pulse' : 'bg-slate-400'
                }`} />
                <span className={`text-sm ${
                  prompt.priority === 'high' ? 'text-amber-800 font-medium' : 'text-slate-600'
                }`}>
                  {prompt.message}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Routine Blocks - Bento Grid */}
        <h2 id="tasks-section" className="font-heading text-base md:text-xl font-semibold text-[#1e3a5f] mb-4 md:mb-6 scroll-mt-20">
          Daily Routine
        </h2>
        
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 mb-6 md:mb-10 stagger-children">
          {ROUTINE_BLOCKS.map((block) => {
            const blockTasks = tasks[block.id] || [];
            const stats = categoryStats[block.id] || {};
            const completed = stats.completed || blockTasks.filter(t => t.status === 'completed').length;
            const dailyTarget = stats.daily_target || block.target;
            const limitReached = completed >= dailyTarget;
            const Icon = block.icon;
            
            return (
              <div 
                key={block.id}
                className={`bg-white rounded-xl md:rounded-2xl border shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200 ${
                  limitReached ? 'border-emerald-200' : 'border-slate-100'
                }`}
                data-testid={`routine-block-${block.id}`}
              >
                {/* Block Header */}
                <div className={`${limitReached ? 'bg-emerald-50' : block.lightColor} px-4 md:px-6 py-3 md:py-4 border-b border-slate-100`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
                      <div className={`w-8 h-8 md:w-10 md:h-10 ${limitReached ? 'bg-emerald-500' : block.color} rounded-lg md:rounded-xl flex items-center justify-center flex-shrink-0`}>
                        {limitReached ? (
                          <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-white" />
                        ) : (
                          <Icon className="w-4 h-4 md:w-5 md:h-5 text-white" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className={`font-heading font-semibold text-sm md:text-base truncate ${limitReached ? 'text-emerald-700' : block.textColor}`}>
                          {block.title}
                        </h3>
                        <p className="text-xs md:text-sm text-slate-500 truncate">{block.description}</p>
                      </div>
                    </div>
                    <div className={`px-2 md:px-3 py-1 md:py-1.5 rounded-full text-xs md:text-sm font-bold flex-shrink-0 ${
                      limitReached 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : `${block.lightColor} ${block.textColor}`
                    }`}>
                      {completed}/{dailyTarget}
                      {limitReached && ' ✓'}
                    </div>
                  </div>
                  {/* Progress Bar */}
                  <div className="mt-2 md:mt-3 h-1 md:h-1.5 bg-white/50 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-500 ${limitReached ? 'bg-emerald-500' : block.color}`}
                      style={{ width: `${Math.min(100, (completed / dailyTarget) * 100)}%` }}
                    />
                  </div>
                </div>
                
                {/* Tasks List - SCROLLABLE */}
                <div className="p-4 max-h-[300px] overflow-y-auto">
                  {limitReached && (
                    <div className="flex items-center justify-center gap-2 py-3 mb-4 bg-emerald-50 rounded-xl text-emerald-700">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="font-medium">Target Complete!</span>
                    </div>
                  )}
                  
                  {blockTasks.length > 0 ? (
                    <div className="space-y-2 mb-4">
                      {blockTasks.map((task) => (
                        <div 
                          key={task.id}
                          className={`flex items-center gap-3 p-3 rounded-xl transition-colors group ${
                            limitReached && task.status !== 'completed'
                              ? 'bg-slate-100 opacity-60'
                              : 'bg-slate-50 hover:bg-slate-100'
                          }`}
                        >
                          <Checkbox
                            checked={task.status === 'completed'}
                            onCheckedChange={() => toggleTask(task.id, task.status)}
                            className="border-slate-300 flex-shrink-0"
                            disabled={limitReached && task.status !== 'completed'}
                            data-testid={`task-checkbox-${task.id}`}
                          />
                          <span className={`flex-1 text-sm ${task.status === 'completed' ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                            {task.title}
                          </span>
                          {task.status === 'completed' ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteTask(task.id);
                              }}
                              className="p-1 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
                              data-testid={`delete-task-${task.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-4">
                      No tasks yet
                    </p>
                  )}
                  
                  {/* Mini Flows */}
                  <div className="border-t border-slate-100 pt-4">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">
                      Quick Actions
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {(customQuickActions?.[block.id] || block.miniFlows).map((flow, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            if (!limitReached) {
                              createQuickTask(block.id, flow.action);
                              logAction(`${block.id}_action`, block.id, { action: flow.action });
                            }
                          }}
                          disabled={limitReached}
                          className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                            limitReached
                              ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                          data-testid={`quick-action-${block.id}-${idx}`}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          {flow.action}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Recent Activity */}
        <h2 className="font-heading text-xl font-semibold text-[#1e3a5f] mb-6">
          Recent Activity
        </h2>
        
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          {recentActions.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {recentActions.map((action, idx) => (
                <div key={idx} className="px-6 py-4 flex items-center gap-4">
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                    <Clock className="w-4 h-4 text-slate-500" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-slate-700">
                      <span className="font-medium capitalize">{action.action_type.replace('_', ' ')}</span>
                      {' '}<span className="text-slate-500">on {action.entity_type}</span>
                    </p>
                    <p className="text-xs text-slate-400">
                      {new Date(action.timestamp).toLocaleTimeString('en-GB', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-6 py-10 text-center text-slate-400">
              No recent activity
            </div>
          )}
        </div>
      </div>

      {/* Add Reminder Modal */}
      <Dialog open={showReminderModal} onOpenChange={setShowReminderModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading text-[#1e3a5f]">
              <Bell className="w-5 h-5" />
              Add Daily Reminder
            </DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              addReminder();
            }}
            className="space-y-4 pt-2"
          >
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Reminder
              </label>
              <Input
                placeholder="e.g., Call back John at 2pm"
                value={reminderForm.title}
                onChange={(e) => setReminderForm(prev => ({ ...prev, title: e.target.value }))}
                className="rounded-xl"
                data-testid="reminder-title-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Time (optional)
              </label>
              <Input
                type="time"
                value={reminderForm.time}
                onChange={(e) => setReminderForm(prev => ({ ...prev, time: e.target.value }))}
                className="rounded-xl"
                data-testid="reminder-time-input"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowReminderModal(false)}
                className="flex-1 rounded-full"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 rounded-full bg-[#1e3a5f] hover:bg-[#162c46]"
                disabled={!reminderForm.title.trim()}
                data-testid="save-reminder-btn"
              >
                Save Reminder
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};
